// frontend/src/pages/ContasPage/ContasPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getContas, deleteConta, arquivarConta, reativarConta } from '../../services/accountService';
import './ContasPage.css';

import LogoManager from '../../components/LogoManager/LogoManager';
import EmptyStateGuide from '../../components/EmptyStateGuide/EmptyStateGuide';
import { Button, Card, Row, Col, Statistic, Avatar, Switch, Typography, message, Modal, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, BankOutlined, WalletOutlined, GoldOutlined, SaveOutlined, ExclamationCircleOutlined, InboxOutlined, RollbackOutlined, RightOutlined } from '@ant-design/icons';
import ContaModal from '../../components/ContaModal/ContaModal';
import { getErrorMessage } from '../../utils/errorHandler';
import { useDataRefresh } from '../../contexts/DataRefreshContext';

const { Title, Text } = Typography;

const ContasPage = () => {
    const [contas, setContas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedConta, setSelectedConta] = useState(null);
    const [showArchived, setShowArchived] = useState(false);
    const navigate = useNavigate();
    const { refreshKey } = useDataRefresh();
    
    // Hooks do Ant Design
    const [messageApi, contextHolder] = message.useMessage();
    const [modal, modalContextHolder] = Modal.useModal();

    // Hook de Responsividade
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const carregarContas = useCallback(async () => {
        setLoading(true);
        try {
            const status = showArchived ? 'ARQUIVADO' : 'ATIVO';
            const data = await getContas({ status });
            setContas(data);
        } catch (error) {
            console.error("Falha ao carregar contas", error);
            messageApi.error(getErrorMessage(error, 'Não foi possível carregar as contas.'));
        } finally {
            setLoading(false);
        }
    }, [showArchived, messageApi]);

    useEffect(() => {
        carregarContas();
    }, [carregarContas, refreshKey]);

    useEffect(() => {
        const handleTransactionUpdate = () => {
            carregarContas();
        };
        window.addEventListener('transaction-updated', handleTransactionUpdate);
        return () => {
            window.removeEventListener('transaction-updated', handleTransactionUpdate);
        };
    }, [carregarContas]);

    const saldoTotal = useMemo(() => {
        if (showArchived) return 0;
        return contas.reduce((acc, conta) => acc + parseFloat(conta.saldo_atual || 0), 0);
    }, [contas, showArchived]);

    const handleOpenModal = (conta = null) => {
        setSelectedConta(conta);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setSelectedConta(null);
        setIsModalOpen(false);
    };

    const handleSave = (shouldClose = true, successMsg = null) => {
        if (successMsg) messageApi.success(successMsg);
        
        carregarContas();
        
        if (shouldClose !== false) {
             handleCloseModal();
        }
    };

    const handleAction = async (action, contaId, successMessageOrBuilder) => {
        try {
            const result = await action(contaId);
            const successMessage = typeof successMessageOrBuilder === 'function'
                ? successMessageOrBuilder(result)
                : successMessageOrBuilder;
            messageApi.success(successMessage);
            carregarContas();
        } catch (err) {
            const errorMsg = getErrorMessage(err, 'Ocorreu um erro ao executar a ação.');
            messageApi.error(errorMsg);
        }
    };

    const showConfirmArquivar = (conta, hasTransactions) => {
        const actionVerb = hasTransactions ? 'arquivar' : 'excluir';

        const actionFunction = () => handleAction(
            deleteConta,
            conta.id,
            (result) => result?.archived
                ? 'Conta arquivada com sucesso!'
                : 'Conta excluída com sucesso!'
        );

        modal.confirm({
            title: `Deseja ${actionVerb} esta conta?`,
            icon: <ExclamationCircleOutlined />,
            content: hasTransactions 
                ? 'A conta será movida para arquivados para preservar o histórico financeiro.' 
                : 'Esta ação removerá a conta permanentemente.',
            okText: `Sim, ${actionVerb}`,
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: actionFunction,
        });
    };

    return (
        <div style={{ padding: isMobile ? 0 : '24px', paddingBottom: isMobile ? 0 : '2rem' }}>
            {contextHolder}
            {modalContextHolder}
            
            {/* Header Responsivo */}
            <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: isMobile ? 16 : 24 }}>
                <Col xs={24} md={12}>
                    <Title level={isMobile ? 3 : 2} style={{ margin: 0 }}>Minhas Contas</Title>
                </Col>
                <Col xs={24} md={12}>
                    <div style={{ 
                        display: 'flex', 
                        gap: 16, 
                        justifyContent: isMobile ? 'flex-start' : 'flex-end', 
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'stretch' : 'center'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: isMobile ? 'space-between' : 'flex-start',
                            background: isMobile ? '#fff' : 'transparent',
                            padding: isMobile ? '8px 12px' : 0,
                            borderRadius: isMobile ? '8px' : 0,
                            border: isMobile ? '1px solid var(--color-border, #E8E2D6)' : 'none'
                        }}>
                            <Text>Exibir arquivados</Text>
                            <Switch checked={showArchived} onChange={setShowArchived} />
                        </div>
                        
                        <Button 
                            type="primary" 
                            icon={<PlusOutlined />} 
                            onClick={() => handleOpenModal()}
                            size={isMobile ? 'large' : 'middle'}
                            block={isMobile}
                        >
                            Adicionar Conta
                        </Button>
                    </div>
                </Col>
            </Row>

            {loading ? (
                <div className="brand-skeleton" style={{ padding: 24 }} aria-busy="true" aria-label="Carregando">
  <div className="brand-skeleton-line brand-skeleton-line--lg" />
  <div className="brand-skeleton-line brand-skeleton-line--md" />
  <div className="brand-skeleton-line" />
  <div className="brand-skeleton-line brand-skeleton-line--sm" />
</div>
            ) : (
                <>
                    {!showArchived && (
                        isMobile ? (
                            <div className="contas-hero-mobile">
                                <div className="contas-hero-mobile__label">Saldo total</div>
                                <div
                                    className="contas-hero-mobile__value"
                                    style={{ color: saldoTotal >= 0 ? '#2F9E6F' : '#D64545' }}
                                >
                                    {formatCurrency(saldoTotal)}
                                </div>
                            </div>
                        ) : (
                        <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
                            <Col span={24}>
                                <Card bodyStyle={{ padding: '24px' }}>
                                    <Statistic
                                        title="Saldo Total em Contas Ativas"
                                        value={saldoTotal}
                                        formatter={formatCurrency}
                                        valueStyle={{ color: saldoTotal >= 0 ? '#2F9E6F' : '#D64545', fontSize: '1.75rem' }}
                                    />
                                </Card>
                            </Col>
                        </Row>
                        )
                    )}

                    {contas.length === 0 ? (
                        <EmptyStateGuide
                            title={showArchived ? 'Nenhuma conta arquivada.' : 'Nenhuma conta cadastrada.'}
                            description={
                                showArchived
                                    ? 'Contas arquivadas aparecem aqui para preservar o histórico.'
                                    : 'Cadastre suas contas bancárias ou carteiras para acompanhar saldos e extratos.'
                            }
                            actionLabel={showArchived ? undefined : 'Adicionar primeira conta'}
                            onAction={showArchived ? undefined : () => handleOpenModal()}
                        />
                    ) : isMobile ? (
                        <div className="dense-list">
                            {contas.map((conta) => {
                                const hasTransactions = Boolean(conta.tem_movimentacao);
                                const saldo = parseFloat(conta.saldo_atual || 0);
                                return (
                                    <div
                                        key={conta.id}
                                        role="button"
                                        tabIndex={0}
                                        className={`dense-list__row${conta.status === 'ARQUIVADO' ? ' dense-list__row--archived' : ''}`}
                                        onClick={() => navigate(`/contas/${conta.id}`)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                navigate(`/contas/${conta.id}`);
                                            }
                                        }}
                                    >
                                        <div className="dense-list__avatar">
                                            {conta.banco && conta.banco.slug ? (
                                                <LogoManager slug={conta.banco.slug} />
                                            ) : (
                                                <Avatar size="large" icon={tipoContaIconMap[conta.tipo_conta]} />
                                            )}
                                        </div>
                                        <div className="dense-list__main">
                                            <div className="dense-list__title">{conta.nome}</div>
                                            <div className="dense-list__sub">
                                                {conta.banco ? conta.banco.nome : conta.tipo_conta?.replace('_', ' ')}
                                            </div>
                                        </div>
                                        <div className="dense-list__meta">
                                            <div className={`dense-list__value${saldo < 0 ? ' dense-list__value--neg' : ''}`}>
                                                {formatCurrency(saldo)}
                                            </div>
                                            <div className="dense-list__actions">
                                                <button
                                                    type="button"
                                                    className="dense-list__action"
                                                    aria-label="Editar"
                                                    onClick={(e) => { e.stopPropagation(); handleOpenModal(conta); }}
                                                >
                                                    <EditOutlined />
                                                </button>
                                                {conta.status === 'ATIVO' ? (
                                                    <button
                                                        type="button"
                                                        className="dense-list__action dense-list__action--danger"
                                                        aria-label={hasTransactions ? 'Arquivar' : 'Excluir'}
                                                        onClick={(e) => { e.stopPropagation(); showConfirmArquivar(conta, hasTransactions); }}
                                                    >
                                                        {hasTransactions ? <InboxOutlined /> : <DeleteOutlined />}
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="dense-list__action dense-list__action--success"
                                                        aria-label="Reativar"
                                                        onClick={(e) => { e.stopPropagation(); handleAction(reativarConta, conta.id, 'Conta reativada com sucesso!'); }}
                                                    >
                                                        <RollbackOutlined />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <RightOutlined className="dense-list__chevron" />
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                    <Row gutter={[24, 24]}>
                        {contas.map(conta => {
                            const hasTransactions = Boolean(conta.tem_movimentacao);

                            const cardActions = [];

                            cardActions.push(
                                <Tooltip title="Editar dados da conta">
                                    <div onClick={(e) => { e.stopPropagation(); handleOpenModal(conta); }} className="conta-action-btn">
                                        <EditOutlined key="edit" style={{ fontSize: '20px' }} />
                                        <span style={{ marginLeft: 8 }}>Editar</span>
                                    </div>
                                </Tooltip>
                            );

                            if (conta.status === 'ATIVO') {
                                cardActions.push(
                                    <Tooltip title={hasTransactions ? "Arquivar conta" : "Excluir conta permanentemente"}>
                                        <div onClick={(e) => { e.stopPropagation(); showConfirmArquivar(conta, hasTransactions); }} className="conta-action-btn danger">
                                            {hasTransactions ? (
                                                <>
                                                    <InboxOutlined key="archive" style={{ fontSize: '20px' }} />
                                                    <span style={{ marginLeft: 8 }}>Arquivar</span>
                                                </>
                                            ) : (
                                                <>
                                                    <DeleteOutlined key="delete" style={{ fontSize: '20px' }} />
                                                    <span style={{ marginLeft: 8 }}>Excluir</span>
                                                </>
                                            )}
                                        </div>
                                    </Tooltip>
                                );
                            } else {
                                cardActions.push(
                                    <Tooltip title="Reativar conta">
                                        <div onClick={(e) => { e.stopPropagation(); handleAction(reativarConta, conta.id, 'Conta reativada com sucesso!'); }} className="conta-action-btn success">
                                            <RollbackOutlined key="reactivate" style={{ fontSize: '20px' }} />
                                            <span style={{ marginLeft: 8 }}>Reativar</span>
                                        </div>
                                    </Tooltip>
                                );
                            }

                            return (
                                <Col xs={24} sm={12} md={8} lg={6} key={conta.id}>
                                    <Card
                                        hoverable
                                        className={`conta-card ${conta.status === 'ARQUIVADO' ? 'archived' : ''}`}
                                        onClick={() => navigate(`/contas/${conta.id}`)}
                                        actions={cardActions}
                                        bodyStyle={{ padding: '16px' }}
                                    >
                                        <Card.Meta
                                            avatar={
                                                conta.banco && conta.banco.slug ?
                                                <LogoManager slug={conta.banco.slug} /> :
                                                <Avatar size="large" icon={tipoContaIconMap[conta.tipo_conta]} />
                                            }
                                            title={conta.nome}
                                            description={conta.banco ? conta.banco.nome : conta.tipo_conta?.replace('_', ' ')}
                                        />
                                        <div className="conta-saldo-container">
                                            <Statistic value={conta.saldo_atual} formatter={formatCurrency} valueStyle={{ fontSize: '1.25rem' }} />
                                        </div>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                    )}
                </>
            )}

            <ContaModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSave}
                contaToEdit={selectedConta}
                isMobile={isMobile}
            />
        </div>
    );
}

const tipoContaIconMap = {
    'conta_corrente': <BankOutlined />,
    'poupanca': <SaveOutlined />,
    'carteira': <WalletOutlined />,
    'investimento': <GoldOutlined />,
    'outros': <WalletOutlined />
};

const formatCurrency = (value) => {
    const number = parseFloat(value) || 0;
    return number.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

export default ContasPage;